import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { handleIlSessionExpiry } from '../config/ilHandoff';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Global logout handler that can be set by AuthContext 1
let globalLogoutHandler: (() => void) | null = null;

export const setGlobalLogoutHandler = (handler: () => void) => {
  globalLogoutHandler = handler;
};

// Preserve the real toast.error so we can restore it after login
const originalToastError = toast.error.bind(toast);

// Track whether session expiry has already been handled this session
let sessionExpired = false;

// A second, independent guard against handling the same expiry twice.
//
// sessionExpired cannot do that job on its own: handleSessionExpired calls the
// logout handler, which calls resetApiState(), which sets sessionExpired back
// to false - and it has to, because the api client must work again for the
// login request that follows. So when a screen fires several requests at once
// and they all come back 401, each one found the flag cleared by the previous
// one and raised its own toast and its own logout. This timestamp survives the
// reset, so the burst is handled once.
const EXPIRY_NOTICE_WINDOW_MS = 10 * 1000;
let expiryHandledAt = 0;

function handleSessionExpired(message = 'Your session has expired. Please login again.') {
  const now = Date.now();
  if (sessionExpired || now - expiryHandledAt < EXPIRY_NOTICE_WINDOW_MS) return;
  expiryHandledAt = now;
  sessionExpired = true;

  // Show the single "session expired" notification
  originalToastError(message);

  // Suppress all subsequent toast.error calls so page-level catch blocks
  // don't stack additional "Failed to load X" messages on top
  (toast as any).error = () => '';

  // Read before clearing: the fallback below must only fire for a session that
  // was actually signed in, never for a public page that happened to get a 401.
  const wasAuthenticated = !!localStorage.getItem('token');

  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem(SESSION_START_KEY);

  // On the Israel-tenant host there is no /login to land on: those users have
  // no password here. A hard navigation back to the Israeli system, carrying
  // the page they were on, is what signs them in again - and because their
  // clinicUI session is usually still alive, it normally happens without their
  // noticing. Storage is already cleared above, so skipping the logout handler
  // loses nothing.
  if (wasAuthenticated && handleIlSessionExpiry()) return;

  // The logout handler clears the user, which swaps the app to the
  // unauthenticated routes and navigates to /login.
  let redirected = false;
  if (globalLogoutHandler) {
    try {
      globalLogoutHandler();
      redirected = true;
    } catch (logoutError) {
      console.error('Error during logout:', logoutError);
    }
  }

  // Nothing registered, or it threw. Leaving somebody on a page that looks
  // alive but silently rejects every request is the worst outcome here - it
  // reads as the app being broken, not as being signed out - so fall back to a
  // hard navigation. It also discards the in-memory state the dead session
  // left behind.
  if (!redirected && wasAuthenticated && typeof window !== 'undefined') {
    window.location.replace('/login');
  }
}

// Reset module-level state on logout so the next login attempt isn't blocked
/**
 * Start a fresh session clock. Call this the moment a login succeeds.
 *
 * lastActivityTime is module state, set once when the bundle loads. A tab left
 * sitting on the login page for over 30 minutes therefore started its *next*
 * session already past the idle limit: the first authenticated request tripped
 * the backstop below, handleSessionExpired cleared the just-issued token, and
 * every request after that went out with no Authorization header - surfacing
 * as "Not authenticated" seconds after signing in. The 30 minutes have to run
 * from the login, not from whenever the page happened to load.
 */
export const markSessionStart = () => {
  const now = Date.now();
  persistActivity(now);
  try {
    localStorage.setItem(SESSION_START_KEY, String(now));
  } catch {
    // Private mode or a storage-blocked browser: isIdle falls back to the clock.
  }
  sessionExpired = false;
  expiryHandledAt = 0;
};

export const resetApiState = () => {
  isRefreshing = false;
  failedQueue.forEach((prom) => prom.reject(new Error('Session reset')));
  failedQueue = [];
  persistActivity(Date.now());
  try {
    localStorage.removeItem(SESSION_START_KEY);
  } catch {
    // ignore - the key only ever protects a session, never ends one
  }
  sessionExpired = false;
  (toast as any).error = originalToastError;
};

// Track last activity time for idle timeout
let lastActivityTime = Date.now();
// 30 minutes, matching IDLE_TIMEOUT_MINUTES on the server. The two used to
// disagree - 15 here against 60 there - which made the behaviour hard to
// reason about: whichever clock ran out first decided, and they measure
// different things (this one real user activity, the server authenticated
// requests). Keep them equal.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// The clock is shared across tabs, because the token is.
//
// lastActivityTime above is module state, so each tab has its own; token and
// refreshToken live in localStorage, so every tab shares those. A second tab
// left open and untouched for half an hour therefore ran its own watchdog,
// decided the session was idle, and cleared the shared token - signing the
// user out of the tab they were actively working in, mid-click. Persisting the
// timestamp means activity anywhere keeps every tab alive, and no tab can
// expire a session another tab is using.
const LAST_ACTIVITY_KEY = 'lastActivityTime';

const persistActivity = (ts: number) => {
  lastActivityTime = ts;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  } catch {
    // Private mode or a storage-blocked browser: fall back to this tab only.
  }
};

/** The most recent activity in ANY tab, never older than this tab's own. */
const readLastActivity = (): number => {
  try {
    const stored = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    if (Number.isFinite(stored) && stored > lastActivityTime) return stored;
  } catch {
    // ignore - fall through to this tab's value
  }
  return lastActivityTime;
};

// When the session began, shared like the token is.
//
// The clock above is still something a tab can be wrong about: a tab running an
// older bundle measures only its own, hours-stale, and reads nothing from
// storage; a tab whose write was blocked has only its own; a device whose clock
// jumps forward carries every copy with it. Each ends the same way - a tab
// decides a session it knows nothing about has gone idle and deletes the shared
// token, and the next request leaves without an Authorization header, which the
// server answers 403 "Not authenticated". A session that began less than the
// idle window ago cannot have been idle for the whole window, so no tab is
// entitled to end it. Nothing is given away: a session nobody touches still
// expires, one window after the login rather than one after the last click.
const SESSION_START_KEY = 'sessionStartedAt';

/** When the current session started, or 0 if that is not known. */
const sessionStartedAt = (): number => {
  try {
    const started = Number(localStorage.getItem(SESSION_START_KEY));
    return Number.isFinite(started) ? started : 0;
  } catch {
    return 0;
  }
};

/** Has the session been untouched for the whole idle window? */
const isIdle = (): boolean => {
  if (Date.now() - readLastActivity() <= IDLE_TIMEOUT_MS) return false;
  const started = sessionStartedAt();
  return started === 0 || Date.now() - started > IDLE_TIMEOUT_MS;
};

// "Activity" means the user actually doing something, not the app making HTTP
// calls. Tracking only requests logged people out mid-task: reading a summary,
// typing notes or filling a long form issues no requests, so a working user
// looked idle and got signed out on their next click.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove'] as const;
const ACTIVITY_THROTTLE_MS = 10 * 1000;
const IDLE_CHECK_INTERVAL_MS = 30 * 1000;
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

const markActivity = () => {
  const now = Date.now();
  if (now - lastActivityTime >= ACTIVITY_THROTTLE_MS) {
    persistActivity(now);
  }
};

if (typeof window !== 'undefined') {
  ACTIVITY_EVENTS.forEach((event) =>
    window.addEventListener(event, markActivity, { passive: true, capture: true })
  );

  // A watchdog, not a per-request check: the timeout has to fire while the user
  // is away and doing nothing, otherwise their first event on returning would
  // reset the clock and the session would never expire. Background tabs throttle
  // intervals to roughly once a minute, which is still often enough here.
  window.setInterval(() => {
    if (sessionExpired || !localStorage.getItem('token')) return;
    if (isIdle()) {
      handleSessionExpired('Session expired due to inactivity. Please login again.');
    }
  }, IDLE_CHECK_INTERVAL_MS);

  // The server keeps its own idle clock (users.last_activity_time, 60 min),
  // refreshed only by authenticated requests. A user who is genuinely working
  // but not triggering calls would trip that one instead, so tell the server
  // they are still here — but only when they actually have been active.
  window.setInterval(() => {
    if (sessionExpired || !localStorage.getItem('token')) return;
    if (Date.now() - readLastActivity() > HEARTBEAT_INTERVAL_MS) return;
    api.get('/auth/me').catch(() => {
      /* transport errors are handled by the response interceptor */
    });
  }, HEARTBEAT_INTERVAL_MS);
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add auth token to requests and check idle timeout
api.interceptors.request.use((config) => {
  // If session already expired, silently cancel further requests
  if (sessionExpired) {
    return Promise.reject(new Error('Session expired'));
  }

  const token = localStorage.getItem('token');

  // Skip idle timeout check for auth endpoints (login, refresh, forgot-password, reset-password)
  const isAuthEndpoint = config.url?.includes('/auth/login') ||
                         config.url?.includes('/auth/refresh') ||
                         config.url?.includes('/auth/forgot-password') ||
                         config.url?.includes('/auth/reset-password');

  if (token && !isAuthEndpoint) {
    config.headers.Authorization = `Bearer ${token}`;

    // Backstop for the watchdog above, in case its interval was starved.
    // Deliberately does NOT refresh lastActivityTime: background polling
    // (job status, notifications) would otherwise keep a session alive with
    // nobody at the keyboard.
    if (isIdle()) {
      handleSessionExpired('Session expired due to inactivity. Please login again.');
      return Promise.reject(new Error('Session expired due to inactivity'));
    }
  } else if (token) {
    // For auth endpoints, just add the token without idle check
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Handle auth errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if this is a 401 error and not already a retry
    // Skip session-expired handling for public endpoints that don't require auth
    const isPublicEndpoint = originalRequest.url?.startsWith('/public/');

    // FastAPI's HTTPBearer answers a MISSING Authorization header with 403
    // "Not authenticated", not 401 - so a dropped token used to fall straight
    // through this handler and leave the user on a dead page with no redirect.
    // Only that exact detail counts: an ordinary 403 is a privilege denial and
    // must not log anybody out.
    const detail = (error.response?.data as any)?.detail;
    const isMissingCredentials =
      error.response?.status === 403 && detail === 'Not authenticated';
    const isAuthFailure = error.response?.status === 401 || isMissingCredentials;

    if (isAuthFailure && !originalRequest._retry && !isPublicEndpoint) {
      // Check if the backend returned "Session expired due to inactivity"
      const isIdleTimeout = detail === 'Session expired due to inactivity';

      // Idle by this tab's own reckoning, or by the server's - the server
      // measures authenticated requests against its own clock, so its answer
      // stands whatever this tab believes.
      if (isIdle() || isIdleTimeout) {
        handleSessionExpired('Session expired due to inactivity. Please login again.');
        return Promise.reject(error);
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        resetApiState();
        handleSessionExpired();
        return Promise.reject(error);
      }

      try {
        console.log('Calling refresh endpoint...');
        // Attempt to refresh the token
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Update tokens in localStorage
        localStorage.setItem('token', access_token);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Update the Authorization header
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Update last activity time since we successfully refreshed
        persistActivity(Date.now());

        // Process the queued requests
        processQueue(null, access_token);

        isRefreshing = false;

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        isRefreshing = false;
        handleSessionExpired();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;